import { VariantProps } from 'class-variance-authority';
import { ReactNode, RefObject } from 'react';
import { buttonVariants } from '../ui/button';

export enum DialogFlag {
  /**
   * 无任何选择
   */
  NONE = 0x0000,
  /**
   * 无任何选择，阻止窗口关闭
   */
  PREVENT_DEFAULT = 0x0000,
  /**
   * 点击 '是'
   */
  YES = 0x0001,
  /**
   * 选择 '否'
   */
  NO = 0x0002,
  /**
   * 选择 '确定'
   */
  OK = 0x0004,
  /**
   * 选择 '取消'
   */
  CANCEL = 0x0008,
  /**
   * 选择 '关闭'
   */
  CLOSE = 0x0010,
  /**
   * 拒绝，会附带播放 shakeX 动画
   */
  REJECT = 0x0020,
}

export type DialogFlagResult<T = unknown> = DialogFlag & { returnValue?: T };

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export type DialogId = string | number;

export interface DialogOptions<T = unknown> {
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  flags?: DialogFlag | DialogFlag[];
  yesLabel?: string;
  noLabel?: string;
  okLabel?: string;
  cancelLabel?: string;
  yesProps?: Omit<ButtonProps, 'onClick'>;
  noProps?: Omit<ButtonProps, 'onClick'>;
  okProps?: Omit<ButtonProps, 'onClick'>;
  cancelProps?: Omit<ButtonProps, 'onClick'>;
  onRequestClose?: (
    flag: DialogFlag,
    returnValue?: T
  ) => DialogFlag | void | PromiseLike<DialogFlag | void>;
}

export interface DialogProps<T = unknown> extends DialogOptions<T> {
  id: DialogId;
  resolver: PromiseWithResolvers<DialogFlagResult<T>>;
  dismissedResolver?: PromiseWithResolvers<void>;
  loadingFlag: DialogFlag;
  forwardedRef: RefObject<((flag: DialogFlag) => T | PromiseLike<T>) | null>;
  elementRef: RefObject<HTMLDivElement | null>;
}

export interface DialogContextProps<T = unknown> {
  /**
   * 关闭弹窗
   * @param flag 关闭的标志
   * @param returnValue 返回值
   */
  requestClose(flag: DialogFlag): PromiseLike<void> | void;
  close(flag: DialogFlag, returnValue?: T): PromiseLike<void> | void;
  forwardedRef: RefObject<((flag: DialogFlag) => T | PromiseLike<T>) | null>;
}

export function shakeX(target: Element) {
  return target.animate(
    {
      transform: [
        'translateX(0)',
        'translateX(-8px)',
        'translateX(8px)',
        'translateX(-8px)',
        'translateX(8px)',
        'translateX(0)',
      ],
    },
    {
      duration: 600,
      easing: 'ease-out',
      iterations: 1,
    }
  );
}
